/**
 * Creator home: what to do next, what you've earned, and your link.
 *
 * A new creator sees a checklist first — a media kit with no photo and no
 * prices is invisible to brands, and the list says exactly what's missing.
 * Once it's done the checklist folds away and deals that need them lead.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  arr,
  big,
  bucketOf,
  copyText,
  lowestPrice,
  dealStatusLabel,
  DEAL_STATUS,
  needsYou,
  str,
  whatsappShare,
  type CreatorHomeResponse,
  type DealSummary,
} from '../lib/creator';
import { formatDue, formatINR } from '../lib/format';
import {
  Badge,
  Card,
  ErrorNote,
  Meta,
  PageHero,
  SectionTitle,
  Spinner,
} from '../components/Primitives';
import { Icon, type IconName } from '../components/Icon';
import { CreatorCardPreview } from '../components/Creator';

export function CreatorHomePage() {
  const T = useT();
  const { vendor } = useSession();
  const [data, setData] = useState<CreatorHomeResponse | null>(null);
  const [deals, setDeals] = useState<DealSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [home, d] = await Promise.all([
        api.get<CreatorHomeResponse>('/v1/partner/creator'),
        // Deals are a nice-to-have on this screen: a failure here must not
        // hide the checklist.
        api
          .get<{ deals?: DealSummary[] }>('/v1/partner/creator/deals')
          .catch(() => ({ deals: [] as DealSummary[] })),
      ]);
      setData(home);
      setDeals(arr(d.deals));
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !data) return <Spinner />;
  if (error && !data) return <ErrorNote message={error} onRetry={() => void load()} />;

  const profile = data?.profile ?? null;
  const channels = arr(data?.channels);
  const packages = arr(data?.packages);
  const stats = data?.stats ?? {};
  const publicUrl = data?.publicUrl ?? null;
  const activePackages = packages.filter((p) => p.isActive !== false);
  const fromPrice = lowestPrice(packages);

  type Step = { done: boolean; label: string; sub: string; to: string; icon: IconName };
  const steps: Step[] = [
    {
      done: Boolean(profile?.photoUrl),
      label: T('अपनी फ़ोटो लगाएँ', 'Add your photo'),
      sub: T('चेहरा दिखे तो ब्रांड भरोसा करते हैं', 'Brands trust a face they can see'),
      to: '/creator/kit',
      icon: 'camera',
    },
    {
      done: str(profile?.bio).length >= 30,
      label: T('अपने बारे में लिखें', 'Write your bio'),
      sub: T('AI भी मदद कर सकता है', 'AI can draft it for you'),
      to: '/creator/kit',
      icon: 'edit',
    },
    {
      done: arr(profile?.niches).length > 0,
      label: T('अपनी कैटेगरी चुनें', 'Pick your niches'),
      sub: T(
        'फ़ैशन, फ़ूड, टेक… ब्रांड इसी से ढूँढते हैं',
        'Fashion, food, tech… brands search by these',
      ),
      to: '/creator/kit',
      icon: 'star',
    },
    {
      done: channels.length > 0,
      label: T('अपना चैनल जोड़ें', 'Add a channel'),
      sub: T('Instagram, YouTube… फ़ॉलोअर के साथ', 'Instagram, YouTube… with followers'),
      to: '/creator/kit?tab=channels',
      icon: 'link',
    },
    {
      done: activePackages.length > 0,
      label: T('अपना पहला पैकेज बनाएँ', 'Create your first package'),
      sub: T(
        'एक रील, तीन स्टोरी — साफ़ दाम के साथ',
        'One reel, three stories — with a clear price',
      ),
      to: '/creator/packages',
      icon: 'tag',
    },
    {
      done: Boolean(profile?.isPublic),
      label: T('प्रोफ़ाइल पब्लिक करें', 'Go public'),
      sub: T('तभी ब्रांड आपको ढूँढ पाएँगे', 'So brands can find you'),
      to: '/creator/kit',
      icon: 'eye',
    },
    {
      done: Boolean(vendor?.hasPan),
      label: T('PAN और बैंक जोड़ें', 'Add PAN & bank'),
      sub: T('पेमेंट के लिए ज़रूरी, TDS भी कम', 'Needed to pay you; lowers TDS'),
      to: '/profile/tax',
      icon: 'shield',
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;
  const pct = Math.round((doneCount / steps.length) * 100);

  const actionDeals = deals.filter((d) => needsYou(d.status));
  const openDeals = deals.filter((d) => bucketOf(d.status) === 'active');
  const shareText = T(
    `मेरा मीडिया किट देखें और मेरे साथ कोलैब बुक करें: ${publicUrl ?? ''}`,
    `See my media kit and book a collab with me: ${publicUrl ?? ''}`,
  );

  const name = str(profile?.displayName) || vendor?.contactName || '';

  return (
    <div className="space-y-5">
      <PageHero
        icon="sparkle"
        eyebrow={T('क्रिएटर', 'Creator')}
        title={
          profile
            ? T(`नमस्ते, ${name.split(' ')[0]}`, `Hi, ${name.split(' ')[0]}`)
            : T('आपका क्रिएटर स्टूडियो', 'Your creator studio')
        }
        sub={
          profile
            ? profile.isPublic
              ? T('आपकी प्रोफ़ाइल ब्रांड को दिख रही है।', 'Brands can see your profile.')
              : T(
                  'प्रोफ़ाइल अभी छिपी है — पूरी करके पब्लिक करें।',
                  'Your profile is hidden — finish it and go public.',
                )
            : T(
                'मीडिया किट बनाएँ, अपने पैकेज के दाम लगाएँ, और ब्रांड से सीधे डील पाएँ।',
                'Build your media kit, price your packages, and get deals from brands directly.',
              )
        }
        stats={[
          { label: T('कमाई', 'Earned'), value: formatINR(stats.earnedMicros ?? '0') },
          { label: T('आना बाकी', 'Pending'), value: formatINR(stats.pendingMicros ?? '0') },
          {
            label: T('रेटिंग', 'Rating'),
            value:
              stats.rating != null && (stats.ratingCount ?? 0) > 0
                ? `${Number(stats.rating).toFixed(1)}★`
                : '—',
          },
        ]}
      />

      {error && <ErrorNote message={error} onRetry={() => void load()} />}

      {!allDone && (
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-display text-[17px] font-bold">
                {profile
                  ? T('प्रोफ़ाइल पूरी करें', 'Finish your profile')
                  : T('शुरू करें', 'Get started')}
              </p>
              <p className="text-xs text-muted">
                {T(
                  `${steps.length} में से ${doneCount} हो गए`,
                  `${doneCount} of ${steps.length} done`,
                )}
              </p>
            </div>
            <span
              className="tnum flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold"
              style={{
                background: `conic-gradient(var(--sec-ink) ${pct * 3.6}deg, var(--sec-wash) 0)`,
              }}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-panel">
                {pct}%
              </span>
            </span>
          </div>
          <ul className="mt-3 space-y-1">
            {steps.map((s) => (
              <li key={s.label}>
                <Link
                  to={s.to}
                  className={`flex min-h-[56px] items-center gap-3 rounded-2xl px-2 transition hover:bg-panel-2 ${
                    s.done ? 'opacity-60' : ''
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                      s.done ? 'bg-emerald-500 text-white' : ''
                    }`}
                    style={
                      s.done
                        ? undefined
                        : { background: 'var(--sec-wash)', color: 'var(--sec-ink)' }
                    }
                  >
                    <Icon
                      name={s.done ? 'check' : s.icon}
                      className="h-4 w-4"
                      strokeWidth={s.done ? 2.8 : 2}
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[15px] font-semibold ${s.done ? 'line-through' : ''}`}
                    >
                      {s.label}
                    </span>
                    {!s.done && (
                      <span className="block truncate text-xs text-muted">{s.sub}</span>
                    )}
                  </span>
                  {!s.done && <Icon name="chevron" className="h-5 w-5 text-muted" />}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {profile && (
        <Link to="/opportunities" className="card flex items-center gap-3 p-4" data-sec="opps">
          <span className="icon-tile h-11 w-11">
            <Icon name="globe" className="h-6 w-6" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">
              {T('ब्रांड के कैंपेन', 'Brand campaigns for you')}
            </span>
            <span className="block truncate text-xs text-muted">
              {T(
                'जो कैंपेन आपसे मेल खाते हैं, उन पर अपना दाम भेजें।',
                'Apply with your price to campaigns that fit you.',
              )}
            </span>
          </span>
          <Icon name="chevron" className="h-5 w-5 text-muted" />
        </Link>
      )}

      {actionDeals.length > 0 && (
        <section>
          <SectionTitle count={actionDeals.length}>
            {T('आपका इंतज़ार', 'Needs you')}
          </SectionTitle>
          <ul className="space-y-2.5">
            {actionDeals.slice(0, 5).map((d) => (
              <li key={d.id}>
                <DealRow deal={d} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {profile && (
        <section>
          <SectionTitle>{T('आपका पब्लिक लिंक', 'Your public link')}</SectionTitle>
          <Card className="space-y-3">
            <CreatorCardPreview
              profile={profile}
              channels={channels}
              fromPriceMicros={fromPrice}
            />
            {publicUrl && profile.isPublic ? (
              <>
                <p className="truncate rounded-xl bg-panel-2 px-3 py-2 font-mono text-sm">
                  {publicUrl}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() =>
                      void copyText(publicUrl).then((ok) => {
                        setCopied(ok);
                        window.setTimeout(() => setCopied(false), 2000);
                      })
                    }
                  >
                    <Icon name={copied ? 'check' : 'copy'} className="h-5 w-5" />
                    {copied ? T('कॉपी हो गया', 'Copied') : T('लिंक कॉपी', 'Copy link')}
                  </button>
                  <a
                    href={whatsappShare(shareText)}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                    style={{ backgroundImage: 'linear-gradient(135deg, #25d366, #128c7e)' }}
                  >
                    <Icon name="share" className="h-5 w-5" />
                    WhatsApp
                  </a>
                </div>
                <p className="text-xs text-muted">
                  {T(
                    'इसे Instagram बायो और WhatsApp स्टेटस में लगाएँ — ब्रांड सीधे बुक कर सकेंगे।',
                    'Put it in your Instagram bio and WhatsApp status — brands can book you straight from it.',
                  )}
                </p>
              </>
            ) : (
              <p className="flex items-start gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-live">
                <Icon name="eye" className="mt-0.5 h-4 w-4 shrink-0" />
                {T(
                  'प्रोफ़ाइल पब्लिक करने पर यहाँ आपका लिंक मिलेगा।',
                  'Your shareable link appears here once your profile is public.',
                )}
              </p>
            )}
          </Card>
        </section>
      )}

      <dl className="grid grid-cols-2 gap-3">
        {(
          [
            {
              label: T('चल रही डील', 'Open deals'),
              value: String(stats.openDeals ?? openDeals.length),
              icon: 'jobs',
            },
            {
              label: T('पूरी डील', 'Deals done'),
              value: String(stats.dealsCompleted ?? 0),
              icon: 'check',
            },
            { label: T('चैनल', 'Channels'), value: String(channels.length), icon: 'link' },
            {
              label: T('पैकेज', 'Packages'),
              value: String(activePackages.length),
              icon: 'tag',
            },
          ] satisfies Array<{ label: string; value: string; icon: IconName }>
        ).map((x) => (
          <div key={x.label} className="card p-4">
            <span className="icon-tile mb-2 h-9 w-9">
              <Icon name={x.icon} className="h-5 w-5" />
            </span>
            <dt className="text-xs font-semibold text-muted">{x.label}</dt>
            <dd className="tnum font-display text-lg font-bold leading-tight">{x.value}</dd>
          </div>
        ))}
      </dl>

      <Card className="!p-1.5">
        <ul>
          {(
            [
              {
                to: '/creator/kit',
                label: T('मीडिया किट', 'Media kit'),
                sub: T('फ़ोटो, बायो, चैनल, बेहतरीन काम', 'Photo, bio, channels, best work'),
                icon: 'profile',
              },
              {
                to: '/creator/packages',
                label: T('पैकेज और दाम', 'Packages & prices'),
                sub: T('ब्रांड क्या खरीद सकते हैं', 'What brands can buy from you'),
                icon: 'tag',
              },
              {
                to: '/deals',
                label: T('डील', 'Deals'),
                sub: T('रिक्वेस्ट, ड्राफ़्ट, लाइव लिंक', 'Requests, drafts, live links'),
                icon: 'jobs',
              },
              {
                to: '/payouts',
                label: T('कमाई', 'Earnings'),
                sub: T(
                  'हर पेमेंट, कमीशन और TDS के साथ',
                  'Every payout, with commission and TDS',
                ),
                icon: 'payouts',
              },
            ] satisfies Array<{ to: string; label: string; sub: string; icon: IconName }>
          ).map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="flex min-h-[64px] items-center gap-3 rounded-2xl px-3 transition hover:bg-panel-2"
              >
                <span className="icon-tile h-10 w-10">
                  <Icon name={l.icon} className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{l.label}</span>
                  <span className="block truncate text-xs text-muted">{l.sub}</span>
                </span>
                <Icon name="chevron" className="h-5 w-5 text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      </Card>

      <p className="px-2 text-center text-xs text-muted">
        {T(
          'लुलु हर डील पर 10% कमीशन लेता है। ब्रांड पहले पेमेंट करता है, फिर आप काम शुरू करते हैं।',
          'Lulu keeps 10% of each deal. The brand pays first; you start work after that.',
        )}
      </p>
    </div>
  );
}

/** One deal in a list: who, what, status, money, due. */
export function DealRow({ deal }: { deal: DealSummary }) {
  const { lang } = useI18n();
  const T = useT();
  const meta = DEAL_STATUS[deal.status];
  const brand = deal.counterpart?.name || T('ब्रांड', 'Brand');
  return (
    <Link
      to={`/deals/${deal.id}`}
      className="card flex items-center gap-3 p-3.5 transition active:scale-[.99]"
    >
      {deal.counterpart?.photoUrl ? (
        <img
          src={deal.counterpart.photoUrl}
          alt=""
          className="h-11 w-11 shrink-0 rounded-2xl object-cover"
        />
      ) : (
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-display font-bold"
          style={{ background: 'var(--sec-wash)', color: 'var(--sec-ink)' }}
        >
          {brand.slice(0, 1).toUpperCase()}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-semibold">{deal.title || T('डील', 'Deal')}</span>
          {(deal.unread ?? 0) > 0 && (
            <span className="tnum shrink-0 rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">
              {deal.unread}
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-muted">{brand}</span>
        <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge tone={meta?.tone ?? 'neutral'} pulse={meta?.needsYou}>
            {dealStatusLabel(deal.status, lang)}
          </Badge>
          {deal.dueOn && bucketOf(deal.status) === 'active' && (
            <Meta icon="clock">{formatDue(deal.dueOn, lang)}</Meta>
          )}
        </span>
      </span>
      <span className="shrink-0 text-right">
        {big(deal.grossMicros) > 0n ? (
          <span className="tnum block font-display font-bold">
            {formatINR(big(deal.grossMicros))}
          </span>
        ) : (
          <span className="block text-xs text-muted">{T('दाम तय करें', 'Set price')}</span>
        )}
        <Icon name="chevron" className="ml-auto mt-1 h-4 w-4 text-muted" />
      </span>
    </Link>
  );
}
